import { Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ColumnComponent, DataStateChangeEvent, GridComponent, RowClassArgs } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';

/** Pixel tuning for auto-fit, equalize, and fit-to-host column clamping. */
const ACCUMULATOR_GRID_PIXELS = {
  frequencyAndDurationColumnsMaximumWidthPixelsAfterAutoFit: 160,
  accumulatorNameColumnMinimumWidthPixelsAfterAutoFit: 40,
  clampMinimumWidthPixelsAccumulatorColumnWhenGrouped: 50,
  clampMinimumWidthPixelsAccumulatorColumnWhenUngrouped: 80,
  clampMinimumWidthPixelsServiceNameColumn: 80,
  clampMinimumWidthPixelsFrequencyAndDurationColumns: 52,
  clampMinimumWidthPixelsNumericColumns: 56,
  clampMinimumHostClientWidthPixels: 80,
  clampTableVersusHostWidthGutterPixels: 8,
} as const;

@Component({
  standalone: false,
  selector: 'app-accumulator-grid',
  templateUrl: './accumulator-grid.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    app-accumulator-grid { display: block; width: 100%; max-width: 100%; box-sizing: border-box; }
    .accumulator-grid-host { overflow-x: hidden; overflow-y: auto; box-sizing: border-box; }
    .accumulator-grid-host .k-grid,
    .accumulator-grid-host .k-grid-aria-root { width: 100%; max-width: 100%; }
    .accumulator-grid-host .k-grid-header-wrap,
    .accumulator-grid-host .k-grid-content { width: 100%; max-width: 100%; box-sizing: border-box; }
    .accumulator-grid-host .k-grid-header-table,
    .accumulator-grid-host .k-grid-table,
    .accumulator-grid-host .k-grid-footer-table { width: 100%; max-width: 100%; }
    .defaultAlign .accumulatorUnitColumn, .defaultAlign .accumulatorAmountColumn,
    .defaultAlign .remainingUnitColumn, .defaultAlign .remainingAmountColumn,
    .defaultAlign .accumulatorDurationColumn { text-align: right; }
    .okUnits .accumulatorUnitColumn, .okAmount .accumulatorAmountColumn,
    .okUnitsRemaining .remainingUnitColumn, .okAmountRemaining .remainingAmountColumn,
    .okDuration .accumulatorDurationColumn { background-color: #7d9550; font-weight: 600; }
    .overUnits .accumulatorUnitColumn, .overAmount .accumulatorAmountColumn,
    .overUnitsRemaining .remainingUnitColumn, .overAmountRemaining .remainingAmountColumn,
    .overDuration .accumulatorDurationColumn { background-color: #e96b62; font-weight: 600; }
    .accumulator-group-header-center, .accumulator-header-center { text-align: center !important; }
    .accumulator-group-header-center .k-cell-inner, .accumulator-group-header-center .k-link,
    .accumulator-header-center .k-cell-inner, .accumulator-header-center .k-link,
    .accumulator-header-center .k-column-title { display: flex !important; justify-content: center !important; }
    .accumulator-footer-remaining { display: flex !important; justify-content: space-between !important; width: 100%; }
    /* Hide Kendo group footer row when duration footer has nothing to show (e.g. annually). */
    .accumulator-grid-host .k-grid tr:has(> td .accumulator-group-footer-skip),
    .accumulator-grid-host .k-grid tr:has(> th .accumulator-group-footer-skip) {
      display: none !important;
    }
    .accumulator-service-name-col, td.accumulatorServiceNameColumn { max-width: 22rem; box-sizing: border-box; }
    td.accumulatorServiceNameColumn { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  `]
})
export class AccumulatorGridComponent
{
  /** Only column that can be grouped in this grid (`serviceName`). */
  private static readonly groupField = 'serviceName';
  private static readonly groupCountAggregates = [
    { field: 'serviceName', aggregate: 'count' as const },
  ];

  @ViewChild(GridComponent) public kendoGrid?: GridComponent;
  @ViewChild('gridHost') public gridHostRef?: ElementRef<HTMLElement>;
  @ViewChild('serviceNameCol') public serviceNameColumn?: ColumnComponent;
  @ViewChild('accumulatorNameCol') public accumulatorNameColumn?: ColumnComponent;
  @ViewChild('frequencyCol') public frequencyColumn?: ColumnComponent;
  @ViewChild('durationCol') public durationColumn?: ColumnComponent;
  @ViewChild('maxUnitsCol') public maxUnitsColumn?: ColumnComponent;
  @ViewChild('maxAmountCol') public maxAmountColumn?: ColumnComponent;
  @ViewChild('totalUnitsCol') public totalUnitsColumn?: ColumnComponent;
  @ViewChild('totalAmountCol') public totalAmountColumn?: ColumnComponent;
  @ViewChild('remainingUnitsCol') public remainingUnitsColumn?: ColumnComponent;
  @ViewChild('remainingAmountCol') public remainingAmountColumn?: ColumnComponent;

  @Input() public gridMaxHeight = '100vh';
  @Input() public newTitleKey = 'NEW-TOTAL';
  @Input() public durationLoaded = false;

  public readonly nameColumnWidthPx = 50;

  /** Shared starting width for Frequency → Remaining columns (after Accumulator). */
  public readonly uniformDataColumnWidthPx = 45;

  public viewAccumulators: GridViewManager = null;

  private _groupableServices = false;

  constructor(private translateService: TranslateService) { }

  public get groupableServices(): boolean { return this._groupableServices; }

  public get groupedByServiceName(): boolean
  {
    return this.isGrouped(this.viewAccumulators?.state?.group);
  }

  @Input() public set groupableServices(value: boolean)
  {
    this._groupableServices = value;
    this.ensureGroupAggregates();
  }

  @Input() public set dataView(view: GridViewManager)
  {
    this.viewAccumulators = view;
    this.ensureGroupAggregates();
  }

  /**
   * Call after grid data is set (e.g. from HTTP). When grouped by default, Kendo never runs the
   * {@link accumulatorDataStateChange} path that auto-fits the service name column (that only runs on ungroup).
   * Manual group/ungroup looks correct because ungroup triggers {@link GridComponent.autoFitColumn}. This aligns
   * the first tab open with that layout.
   */
  public afterAccumulatorsDataLoad(): void
  {
    if (!this._groupableServices || !this.viewAccumulators?.data?.length) return;
    this.scheduleColumnLayout();
  }

  public accumulatorDataStateChange(e: DataStateChangeEvent): void
  {
    const vm = this.viewAccumulators;
    if (!vm) return;

    const cur = vm.state;

    let group = (e.group !== undefined ? e.group : cur.group) ?? [];
    group = group.filter((x) => x.field === AccumulatorGridComponent.groupField).slice(0, 1);
    if (this.groupableServices && group.length > 0)
    {
      group = group.map((x) => ({ ...x, aggregates: [...AccumulatorGridComponent.groupCountAggregates] }));
    }

    const nextGroup = group as GroupDescriptor[];
    const wasGrouped = (cur.group ?? []).filter((g) => g.field === AccumulatorGridComponent.groupField).length > 0;
    const willBeGrouped = nextGroup.length > 0;
    const groupingToggled = wasGrouped !== willBeGrouped;

    vm.setState({ ...cur, ...e, group: nextGroup });

    if (this.groupableServices && groupingToggled)
    {
      this.scheduleColumnLayout();
    }
  }

  public accumulatorRowCallback({ dataItem: d }: RowClassArgs): Record<string, boolean>
  {
    const css: Record<string, boolean> = { defaultAlign: true };
    if (d.maxUnits != null)
    {
      css.okUnits = d.totalUnits <= d.maxUnits;
      css.overUnits = d.totalUnits > d.maxUnits;
    }
    if (d.maxAmount != null)
    {
      css.okAmount = d.totalAmount <= d.maxAmount;
      css.overAmount = d.totalAmount > d.maxAmount;
    }
    if (d.remainingUnits != null)
    {
      css.okUnitsRemaining = d.remainingUnits >= 0;
      css.overUnitsRemaining = d.remainingUnits < 0;
    }
    if (d.remainingAmount != null)
    {
      css.okAmountRemaining = d.remainingAmount >= 0;
      css.overAmountRemaining = d.remainingAmount < 0;
    }
    css.okDuration = !d.durationExceeded && d.duration;
    css.overDuration = Boolean(d.durationExceeded);
    return css;
  }

  public hasFirstRowDuration(group: any): boolean
  {
    const row = this.groupFooterFirstRow(group);
    return row != null && row.duration != null && row.duration !== '' && (row.frequencyCode === 'weekly' || row.frequencyCode === 'monthly');
  }

  public getFirstRowFrequencyText(group: any): string
  {
    const row = this.groupFooterFirstRow(group);
    const valid = row != null && row.duration != null && row.duration !== '' && (row.frequencyCode === 'weekly' || row.frequencyCode === 'monthly');
    if (!valid) return null;

    const countedRows = this.durationGroupCount(group);
    const remaining = this.groupRemaining(group, countedRows);
    const singular = Math.abs(remaining) === 1;
    return row.frequencyCode === 'weekly'
      ? this.translateService.instant(singular ? 'WEEK' : 'WEEKS')
      : this.translateService.instant(singular ? 'MONTH' : 'MONTHS');
  }

  public groupRemaining(group: any, count: number): number
  {
    const row = this.groupFooterFirstRow(group);
    const duration = row != null && row.duration != null ? Number(row.duration) : 0;
    return duration - (count != null ? Number(count) : 0);
  }

  /**
   * For duration "remaining" footers: each row counts as 1 only when total units are present and positive.
   */
  public durationGroupCount(group: any): number
  {
    const items = group?.data?.items ?? group?.items;
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, row) => sum + (this.rowCountsForDuration(row) ? 1 : 0), 0);
  }

  /** Matches duration footer weighting: row counts only when totalUnits is set and > 0. */
  public rowCountsForDuration(d: any): boolean
  {
    const u = d?.totalUnits;
    if (u == null) return false;
    const n = Number(u);
    return Number.isFinite(n) && n > 0;
  }

  private groupFooterFirstRow(group: any): any
  {
    return group?.data?.items?.[0] ?? group?.items?.[0];
  }

  /** Runs auto-fit, equalize, preferred mins, then shrinks columns so the table fits the host (grouped or not). */
  private scheduleColumnLayout(): void
  {
    const run = (): void =>
    {
      try
      {
        this.kendoGrid?.autoFitColumns();
        this.capNarrowColumnsAfterAutoFit();
        this.equalizePostAccumulatorColumnWidths();
        this.widenAccumulatorNameAfterAutoFit();
        this.clampColumnsToHostWidth();
      }
      catch { /* grid teardown */ }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  /**
   * autoFitColumns measures group footer templates; duration/footer text can become very wide.
   * Cap frequency and duration before equalize so other columns can auto-fit usefully.
   */
  private capNarrowColumnsAfterAutoFit(): void
  {
    const max = ACCUMULATOR_GRID_PIXELS.frequencyAndDurationColumnsMaximumWidthPixelsAfterAutoFit;
    for (const column of [this.frequencyColumn, this.durationColumn])
    {
      if (column && !column.hidden && column.width > max) column.width = max;
    }
  }

  /** Frequency + duration share one width (capped); numeric columns share another. */
  private equalizePostAccumulatorColumnWidths(): void
  {
    const cap = ACCUMULATOR_GRID_PIXELS.frequencyAndDurationColumnsMaximumWidthPixelsAfterAutoFit;
    const { narrow, numeric } = this.layoutDataColumns();
    const narrowVisible = narrow.filter((c) => !c.hidden);
    const numericVisible = numeric.filter((c) => !c.hidden);
    if (narrowVisible.length === 0 && numericVisible.length === 0) return;

    if (narrowVisible.length > 0)
    {
      let narrowTarget = Math.max(...narrowVisible.map((c) => c.width), 1);
      narrowTarget = Math.min(narrowTarget, cap);
      narrowVisible.forEach((c) => (c.width = narrowTarget));
    }

    if (numericVisible.length > 0)
    {
      const numericTarget = Math.max(...numericVisible.map((c) => c.width), 1);
      numericVisible.forEach((c) => (c.width = numericTarget));
    }
  }

  private widenAccumulatorNameAfterAutoFit(): void
  {
    const acc = this.accumulatorNameColumn;
    if (!acc) return;
    const minimum = ACCUMULATOR_GRID_PIXELS.accumulatorNameColumnMinimumWidthPixelsAfterAutoFit;
    if (acc.width < minimum) acc.width = minimum;
  }

  /** Narrow + numeric leaf columns used for equalize and clamp (visible only for clamp). */
  private layoutDataColumns(): { narrow: ColumnComponent[]; numeric: ColumnComponent[] }
  {
    const narrow: ColumnComponent[] = [this.frequencyColumn].filter((c): c is ColumnComponent => Boolean(c));
    if (this.durationLoaded && this.durationColumn) narrow.push(this.durationColumn);
    const numeric: ColumnComponent[] = [
      this.maxUnitsColumn,
      this.maxAmountColumn,
      this.totalUnitsColumn,
      this.totalAmountColumn,
      this.remainingUnitsColumn,
      this.remainingAmountColumn,
    ].filter((c): c is ColumnComponent => Boolean(c));
    return { narrow, numeric };
  }

  private columnsEligibleForClamp(): ColumnComponent[]
  {
    const { narrow, numeric } = this.layoutDataColumns();
    const out: ColumnComponent[] = [
      ...numeric.filter((c) => !c.hidden),
      ...narrow.filter((c) => !c.hidden),
    ];
    if (this.accumulatorNameColumn) out.push(this.accumulatorNameColumn);
    if (this.serviceNameColumn && !this.serviceNameColumn.hidden && !this.groupedByServiceName)
    {
      out.push(this.serviceNameColumn);
    }
    return out;
  }

  private minClampWidthPx(col: ColumnComponent): number
  {
    if (col === this.accumulatorNameColumn)
    {
      return this.groupedByServiceName
        ? ACCUMULATOR_GRID_PIXELS.clampMinimumWidthPixelsAccumulatorColumnWhenGrouped
        : ACCUMULATOR_GRID_PIXELS.clampMinimumWidthPixelsAccumulatorColumnWhenUngrouped;
    }
    if (col === this.serviceNameColumn)
    {
      return ACCUMULATOR_GRID_PIXELS.clampMinimumWidthPixelsServiceNameColumn;
    }
    if (col === this.frequencyColumn || col === this.durationColumn)
    {
      return ACCUMULATOR_GRID_PIXELS.clampMinimumWidthPixelsFrequencyAndDurationColumns;
    }
    return ACCUMULATOR_GRID_PIXELS.clampMinimumWidthPixelsNumericColumns;
  }

  /** Shrink columns until the table fits the host: proportional integer cuts, then one reflow pass. */
  private clampColumnsToHostWidth(): void
  {
    const host = this.gridHostRef?.nativeElement;
    if (!host || host.clientWidth < ACCUMULATOR_GRID_PIXELS.clampMinimumHostClientWidthPixels) return;

    const table = host.querySelector('.k-grid-table') as HTMLElement | null;
    if (!table) return;

    const gutter = ACCUMULATOR_GRID_PIXELS.clampTableVersusHostWidthGutterPixels;
    const shrinkOnce = (): void =>
    {
      const over = table.offsetWidth - host.clientWidth + gutter;
      if (over <= 0) return;

      const pool = this.columnsEligibleForClamp();
      if (pool.length === 0) return;

      const mins = pool.map((c) => this.minClampWidthPx(c));
      const slack = pool.map((c, i) => Math.max(0, c.width - mins[i]));
      const totalSlack = slack.reduce((a, b) => a + b, 0);
      if (totalSlack <= 0) return;

      const remove = Math.min(over, totalSlack);
      const ideal = slack.map((s) => (remove * s) / totalSlack);
      const cuts = ideal.map((v) => Math.floor(v));
      let deficit = remove - cuts.reduce((a, b) => a + b, 0);
      const order = ideal
        .map((v, index) => index)
        .sort((i, j) => ideal[j] - Math.floor(ideal[j]) - (ideal[i] - Math.floor(ideal[i])));

      while (deficit > 0)
      {
        let progressed = false;
        for (const i of order)
        {
          if (deficit <= 0) break;
          if (cuts[i] < slack[i])
          {
            cuts[i]++;
            deficit--;
            progressed = true;
          }
        }
        if (!progressed) break;
      }

      pool.forEach((c, i) => (c.width = Math.max(mins[i], c.width - cuts[i])));
    };

    shrinkOnce();
    requestAnimationFrame(shrinkOnce);
  }

  private isGrouped(group: GroupDescriptor[] | undefined): boolean
  {
    return Array.isArray(group) && group.some((g) => g?.field === AccumulatorGridComponent.groupField);
  }

  private ensureGroupAggregates(): void
  {
    if (!this.groupableServices || !this.viewAccumulators?.state?.group?.length) return;
    const group = this.viewAccumulators.state.group;
    const needsCount = (g: GroupDescriptor) =>
      g.field === AccumulatorGridComponent.groupField &&
      !(Array.isArray(g.aggregates) && g.aggregates.some((a) => a.aggregate === 'count'));
    if (!group.some(needsCount)) return;
    this.viewAccumulators.state.group = group.map((g) =>
      g.field === AccumulatorGridComponent.groupField
        ? { ...g, aggregates: [...AccumulatorGridComponent.groupCountAggregates] }
        : g);
    this.viewAccumulators.setState(this.viewAccumulators.state as DataStateChangeEvent);
  }
}
